import { Controller } from "@hotwired/stimulus"
import {
  buildCalendarMonth,
  calendarEventsFor,
  parseMonth,
  shiftMonth,
  upcomingCalendarEvents
} from "../shared/calendar_month_builder"

export default class extends Controller {
  static values = {
    threshold: { type: Number, default: 60 },
    variant: { type: String, default: "schedule" },
    startMonth: String
  }

  connect() {
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleWheel = this.handleWheel.bind(this)
    this.handlePointerMove = this.handlePointerMove.bind(this)

    this.element.addEventListener("touchstart", this.handleTouchStart, { passive: true })
    this.element.addEventListener("touchmove", this.handleTouchMove, { passive: false })
    this.element.addEventListener("touchend", this.handleTouchEnd, { passive: false })
    this.element.addEventListener("click", this.handleClick, true)
    window.addEventListener("wheel", this.handleWheel, { passive: false, capture: true })
    window.addEventListener("pointermove", this.handlePointerMove, { passive: true })

    const events = calendarEventsFor(this.element)
    this.events = this.variantValue === "service" ? upcomingCalendarEvents(events) : events
    this.currentMonthDate = parseMonth(this.startMonthValue)
    this.currentMonth = this.element.querySelector(".simple-calendar")
    this.wheelDelta = 0
    this.wheelGestureActive = false
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this.handleTouchStart)
    this.element.removeEventListener("touchmove", this.handleTouchMove)
    this.element.removeEventListener("touchend", this.handleTouchEnd)
    this.element.removeEventListener("click", this.handleClick, true)
    window.removeEventListener("wheel", this.handleWheel, true)
    window.removeEventListener("pointermove", this.handlePointerMove)
    window.clearTimeout(this.clickResetTimer)
    window.clearTimeout(this.animationTimer)
    window.clearTimeout(this.wheelResetTimer)
  }

  handleTouchStart(event) {
    if (event.touches.length !== 1 || this.isAnimating) return

    const touch = event.touches[0]
    this.startX = touch.clientX
    this.startY = touch.clientY
    this.deltaX = 0
    this.deltaY = 0
    this.isHorizontalSwipe = false
    this.swipeDirection = null
  }

  handleTouchMove(event) {
    if (event.touches.length !== 1 || this.startX == null) return

    const touch = event.touches[0]
    this.deltaX = touch.clientX - this.startX
    this.deltaY = touch.clientY - this.startY

    if (Math.abs(this.deltaX) > 10 && Math.abs(this.deltaX) > Math.abs(this.deltaY)) {
      this.isHorizontalSwipe = true
      const direction = this.deltaX < 0 ? "next" : "previous"
      if (direction !== this.swipeDirection) this.prepareIncomingMonth(direction)
      this.positionMonths(this.deltaX)
      event.preventDefault()
    }
  }

  handleTouchEnd(event) {
    if (!this.isHorizontalSwipe) {
      this.resetGestureState()
      return
    }

    event.preventDefault()
    this.suppressNextClick = true

    if (Math.abs(this.deltaX) >= this.thresholdValue && this.incomingMonth) {
      this.completeMonthChange()
    } else {
      this.cancelMonthChange()
    }

    this.clickResetTimer = window.setTimeout(() => {
      this.suppressNextClick = false
    }, 500)
    this.resetGestureState()
  }

  handleClick(event) {
    const navigationLink = event.target.closest(".calendar-heading > a")
    if (navigationLink && this.element.contains(navigationLink)) {
      event.preventDefault()
      event.stopImmediatePropagation()
      const links = Array.from(navigationLink.parentElement.querySelectorAll(":scope > a"))
      const direction = navigationLink.dataset.calendarDirection || (navigationLink === links[0] ? "previous" : "next")
      this.navigate(direction)
      return
    }

    if (!this.suppressNextClick) return
    event.preventDefault()
    event.stopImmediatePropagation()
    this.suppressNextClick = false
  }

  handlePointerMove(event) {
    this.pointerX = event.clientX
    this.pointerY = event.clientY
  }

  handleWheel(event) {
    if (!window.matchMedia("(min-width: 768px)").matches || event.ctrlKey) return

    if (event.clientX !== 0 || event.clientY !== 0) {
      this.pointerX = event.clientX
      this.pointerY = event.clientY
    }

    const bounds = this.element.getBoundingClientRect()
    const pointerIsOverCalendar =
      this.pointerX >= bounds.left &&
      this.pointerX <= bounds.right &&
      this.pointerY >= bounds.top &&
      this.pointerY <= bounds.bottom
    if (!pointerIsOverCalendar) return

    const rawDelta = Math.abs(event.deltaX) > 0
      ? event.deltaX
      : event.shiftKey ? event.deltaY : 0
    if (Math.abs(rawDelta) === 0) return

    event.preventDefault()
    event.stopPropagation()
    window.clearTimeout(this.wheelResetTimer)
    this.wheelResetTimer = window.setTimeout(() => {
      this.wheelDelta = 0
      this.wheelGestureActive = false
    }, 180)

    if (this.isAnimating || this.wheelGestureActive) return

    const multiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? this.element.clientWidth : 1
    this.wheelDelta += rawDelta * multiplier
    if (Math.abs(this.wheelDelta) < this.thresholdValue) return

    this.wheelGestureActive = true
    const direction = this.wheelDelta > 0 ? "next" : "previous"
    this.wheelDelta = 0
    this.navigate(direction)
  }

  navigate(direction) {
    if (this.isAnimating) return
    this.prepareIncomingMonth(direction)
    this.positionMonths(0)
    this.completeMonthChange()
  }

  prepareIncomingMonth(direction) {
    this.removeIncomingMonth()
    this.swipeDirection = direction
    const offset = direction === "next" ? 1 : -1
    const month = shiftMonth(this.currentMonthDate, offset)
    const incoming = buildCalendarMonth({
      month,
      events: this.events,
      variant: this.variantValue
    })
    this.mountIncomingMonth(incoming)
  }

  mountIncomingMonth(month) {
    this.incomingMonth = month
    this.incomingMonth.setAttribute("aria-hidden", "true")
    this.incomingMonth.style.position = "absolute"
    this.incomingMonth.style.inset = "0"
    this.incomingMonth.style.width = "100%"
    this.incomingMonth.style.willChange = "transform"
    this.currentMonth.style.willChange = "transform"
    this.element.style.position = "relative"
    this.element.style.overflow = "hidden"
    this.element.appendChild(this.incomingMonth)
  }

  positionMonths(deltaX) {
    if (!this.currentMonth) return

    const width = this.element.clientWidth
    const limitedDelta = Math.max(-width, Math.min(width, deltaX))
    this.currentMonth.style.transform = `translateX(${limitedDelta}px)`

    if (!this.incomingMonth) return
    const startingOffset = this.swipeDirection === "next" ? width : -width
    this.incomingMonth.style.transform = `translateX(${startingOffset + limitedDelta}px)`
  }

  completeMonthChange() {
    if (!this.incomingMonth) return

    const width = this.element.clientWidth
    const outgoingOffset = this.swipeDirection === "next" ? -width : width
    const monthOffset = this.swipeDirection === "next" ? 1 : -1

    this.isAnimating = true
    this.currentMonth.style.transition = "transform 240ms ease-out"
    this.incomingMonth.style.transition = "transform 240ms ease-out"

    requestAnimationFrame(() => {
      this.currentMonth.style.transform = `translateX(${outgoingOffset}px)`
      this.incomingMonth.style.transform = "translateX(0)"
    })

    this.animationTimer = window.setTimeout(() => {
      const outgoingMonth = this.currentMonth
      this.currentMonth = this.incomingMonth
      this.incomingMonth = null
      this.currentMonthDate = parseMonth(shiftMonth(this.currentMonthDate, monthOffset).toISOString())

      outgoingMonth.style.position = "absolute"
      outgoingMonth.style.inset = "0"
      outgoingMonth.style.width = "100%"
      this.resetMonthStyles(this.currentMonth)
      outgoingMonth.remove()
      this.isAnimating = false
    }, 250)
  }

  cancelMonthChange() {
    if (!this.currentMonth) return

    const width = this.element.clientWidth
    const incomingOffset = this.swipeDirection === "next" ? width : -width
    this.isAnimating = true
    this.currentMonth.style.transition = "transform 180ms ease-out"
    this.currentMonth.style.transform = "translateX(0)"

    if (this.incomingMonth) {
      this.incomingMonth.style.transition = "transform 180ms ease-out"
      this.incomingMonth.style.transform = `translateX(${incomingOffset}px)`
    }

    this.animationTimer = window.setTimeout(() => {
      this.resetMonthStyles(this.currentMonth)
      this.removeIncomingMonth()
      this.isAnimating = false
    }, 190)
  }

  resetMonthStyles(month) {
    if (month) {
      month.style.position = ""
      month.style.inset = ""
      month.style.width = ""
      month.style.transform = ""
      month.style.transition = ""
      month.style.willChange = ""
      month.removeAttribute("aria-hidden")
    }

    this.element.style.position = ""
    this.element.style.overflow = ""
  }

  removeIncomingMonth() {
    if (!this.incomingMonth) return
    this.incomingMonth.remove()
    this.incomingMonth = null
  }

  resetGestureState() {
    this.startX = null
    this.startY = null
    this.deltaX = 0
    this.deltaY = 0
    this.isHorizontalSwipe = false
    this.swipeDirection = null
  }
}
